import { useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Badge } from '@/ui/Badge';
import { InputSuelto } from '@/ui/Field';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import { useEmpleados, type Campo, type Empleado } from './api';

/**
 * piezas.tsx — las partes que comparten las siete zonas de Recursos y Datos.
 *
 * ## Lo que arreglan
 *
 * - **El valor de una celda ya no se arma como HTML.** `_fmt` devolvía marcado
 *   —insignia, barra, enlace— y lo insertaba con `innerHTML`, escapando el
 *   valor a mano en cada rama. Aquí es un componente y el texto es texto.
 * - **El color de una insignia sale de los tokens.** Eran tres hexadecimales
 *   con el tinte construido concatenando alfa (`${col}1f`), y el propio código
 *   avisaba de que ese verde no servía como color de texto: 2,87 de contraste.
 * - **El autocompletar de empleados tiene teclado.** Los resultados eran
 *   `<button>` dentro de un `<div>` absoluto, se elegían con un `onclick` que
 *   metía el nombre dentro de otro `onclick` quitándole las comillas simples, y
 *   **cada apertura dejaba un `click` en `document` que nunca se quitaba**.
 */

/* ── El valor de un campo ──────────────────────────────────────────────── */

const ESTADOS: Record<string, 'ok' | 'warn' | 'danger'> = {
  'Vigente': 'ok',
  'Próximo a vencer': 'warn',
  'Vencido': 'danger',
};

/** Un indicador escrito a mano —«Alto», «Crítico»— se lee por su palabra. Es la
 *  misma lista del vanilla, que la usaba para elegir un hexadecimal. */
function tonoDeIndicador(valor: string): 'ok' | 'warn' | 'danger' | null {
  const s = valor.toLowerCase();
  if (/(alto|bueno|óptimo|optimo|verde|ok|cumple)/.test(s)) return 'ok';
  if (/(medio|alerta|amarillo|regular|pendiente)/.test(s)) return 'warn';
  if (/(bajo|malo|rojo|crítico|critico|incumple)/.test(s)) return 'danger';
  return null;
}

export function ValorDeCampo({ campo, valor }: { campo: Campo; valor: unknown }) {
  if (valor == null || valor === '') return <span className="text-muted">—</span>;
  const texto = String(valor);

  if (campo.type === 'email') {
    return (
      <a
        href={`mailto:${texto}`}
        className="text-brand-ink hover:underline focus-visible:focus-anillo"
        onClick={(e) => e.stopPropagation()}
      >
        {texto}
      </a>
    );
  }

  if (campo.type === 'estado' || campo.type === 'indicador') {
    const tono = ESTADOS[texto] ?? tonoDeIndicador(texto);
    if (tono) return <Badge tone={tono}>{texto}</Badge>;
  }

  if (campo.type === 'progreso') {
    const pct = parseInt(texto, 10) || 0;
    return (
      <span className="flex items-center gap-2">
        <span className="min-w-12 flex-1">
          <BarraDeProgreso pct={pct} etiqueta={campo.label} />
        </span>
        <span className="shrink-0 text-1 tabular-nums text-muted">{texto}</span>
      </span>
    );
  }

  return <>{texto}</>;
}

/* ── Búsqueda ──────────────────────────────────────────────────────────── */

/**
 * El texto de búsqueda, pero solo cuando quien escribe se detiene.
 *
 * Las demás listas del portal no lo necesitan: piden una página corta y dejan
 * la anterior en pantalla. Aquí una sola tabla puede ser el maestro entero con
 * todas sus columnas, y el vanilla ya esperaba 300 ms a propósito.
 */
export function useBusquedaDemorada(texto: string, ms = 300): string {
  const [valor, setValor] = useState(texto.trim());
  useEffect(() => {
    const t = window.setTimeout(() => setValor(texto.trim()), ms);
    return () => window.clearTimeout(t);
  }, [texto, ms]);
  return valor;
}

/* ── Fechas ────────────────────────────────────────────────────────────── */

/** Una marca de tiempo del servidor, en la zona de quien mira.
 *
 *  El vanilla hacía `changed_at.replace('T',' ').slice(0,16)`: eso es la hora
 *  UTC en crudo, cinco horas por delante en Bogotá, y salía así en el historial
 *  de versiones, en las invitaciones y en la revisión. */
export function fechaHora(iso?: string | null): string {
  return iso ? Utils.fmtDatetime(iso) : '—';
}

/** Una columna DATE (`yyyy-mm-dd`), sin correrla un día. */
export function fecha(iso?: string | null): string {
  return iso ? Utils.fmtDateOnly(iso) : '—';
}

/* ── Buscador de empleados ─────────────────────────────────────────────── */

/**
 * El autocompletar de esta pantalla.
 *
 * No es `BuscadorDePersonas` de `components/` a propósito: aquel consulta
 * `/users/directory`, y aquí el contrato es
 * `/private-records/autocomplete/employees`, que es de gestión y trae también a
 * quien ya no está activo —sin eso no se puede abrir el expediente de alguien
 * retirado, que es justo cuando se consulta—.
 */
export function BuscadorDeEmpleado({
  id, etiqueta, valorInicial = '', marcador = 'Escribe un nombre…', alElegir, alEscribir,
}: {
  id: string;
  etiqueta: string;
  valorInicial?: string;
  marcador?: string;
  alElegir: (e: Empleado) => void;
  /** Se avisa de cada tecla: escribir a mano invalida la elección anterior, que
   *  es la regla que el vanilla aplicaba con un campo oculto. */
  alEscribir?: (texto: string) => void;
}) {
  const [texto, setTexto] = useState(valorInicial);
  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(0);
  const campo = useRef<HTMLInputElement>(null);
  const idLista = useId();
  const { data, isFetching } = useEmpleados(texto);
  const resultados = data ?? [];

  function elegir(e: Empleado) {
    alElegir(e);
    setTexto(e.label);
    setAbierto(false);
    campo.current?.focus();
  }

  return (
    <div className="relative">
      <InputSuelto
        ref={campo}
        id={id}
        role="combobox"
        aria-expanded={abierto && resultados.length > 0}
        aria-controls={idLista}
        aria-autocomplete="list"
        aria-label={etiqueta}
        autoComplete="off"
        value={texto}
        placeholder={marcador}
        onChange={(e) => {
          setTexto(e.target.value);
          setAbierto(true);
          setResaltado(0);
          alEscribir?.(e.target.value);
        }}
        onKeyDown={(e) => {
          if (!resultados.length) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setResaltado((i) => (i + 1) % resultados.length);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setResaltado((i) => (i - 1 + resultados.length) % resultados.length);
          } else if (e.key === 'Enter' && abierto) {
            e.preventDefault();
            const elegido = resultados[resaltado];
            if (elegido) elegir(elegido);
          } else if (e.key === 'Escape') {
            setAbierto(false);
          }
        }}
        onBlur={() => {
          // Un poco de margen para que el clic en un resultado llegue antes de
          // que la lista se cierre.
          window.setTimeout(() => setAbierto(false), 120);
        }}
      />

      {abierto && texto.trim().length >= 2 && (
        <ul
          id={idLista}
          role="listbox"
          aria-label={etiqueta}
          className="absolute top-full z-30 mt-1 max-h-48 w-full overflow-y-auto rounded-sm border border-line bg-surface shadow-raised"
        >
          {isFetching && !resultados.length ? (
            <li className="px-3 py-2 text-2 text-muted">Buscando…</li>
          ) : !resultados.length ? (
            <li className="px-3 py-2 text-2 text-muted">Sin resultados</li>
          ) : (
            resultados.map((e, i) => (
              <li key={e.id} role="option" aria-selected={i === resaltado}>
                <button
                  type="button"
                  className={cn(
                    'flex w-full items-baseline gap-2 px-3 py-2 text-left text-3',
                    i === resaltado ? 'bg-brand/8 text-brand-ink' : 'hover:bg-sunken',
                  )}
                  onMouseEnter={() => setResaltado(i)}
                  onClick={() => elegir(e)}
                >
                  <span className="min-w-0 flex-1 truncate">{e.label}</span>
                  {e.sub && <span className="shrink-0 text-1 text-muted">{e.sub}</span>}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

/* ── Cabecera con vuelta ───────────────────────────────────────────────── */

export function CabeceraDeZona({
  volverA = '#recursos-privados', volverTexto = 'Recursos', icono, titulo, children,
}: {
  volverA?: string;
  volverTexto?: string;
  icono: string;
  titulo: React.ReactNode;
  /** Los botones de la derecha. */
  children?: React.ReactNode;
}) {
  return (
    <header className="mb-4 flex flex-wrap items-center gap-3">
      <a
        href={volverA}
        className="inline-flex items-center gap-1 text-3 font-semibold text-brand-ink hover:underline focus-visible:focus-anillo"
      >
        <i className="bi bi-arrow-left" aria-hidden="true" /> {volverTexto}
      </a>
      <h1 className="flex min-w-0 items-center gap-2 text-5 font-bold text-brand-ink">
        <i className={cn('bi', icono)} aria-hidden="true" /> {titulo}
      </h1>
      {children && <div className="ml-auto flex flex-wrap gap-2">{children}</div>}
    </header>
  );
}
